import { IssueBillingDocument } from "./Models";
import { FgoClientType, FgoCurrency } from "./Types";

/**
 * Maps the invoice payload to FGO document structure
 * @param data - The IssueBillingDocument data from InvoicePanel
 * @param documentType - The document type (from form: "Factura", "Aviz", etc.)
 * @param fgoNomenclature - Optional FGO nomenclature data (TipFactura, Valuta, etc.)
 * @returns FGO-formatted payload
 */
export const mapToFgoDocument = (
  data: IssueBillingDocument,
  documentType?: string,
  fgoNomenclature?: {
    tipFactura?: any;
    valuta?: any;
    tipClient?: any;
    tva?: any;
  }
) => {
  const { invoiceModel, order } = data;

  if (!invoiceModel || !order) {
    throw new Error("Invalid data: invoiceModel and order are required");
  }

  // Determine client type based on fiscal code presence
  const clientType = invoiceModel.client?.cif
    ? FgoClientType.PJ
    : FgoClientType.PF;

  // Map client data (using Romanian field names for FGO API)
  const fgoClient = {
    Denumire: invoiceModel.client?.name || "",
    CodUnic: invoiceModel.client?.cif || undefined,
    RegCom: invoiceModel.client?.rc || undefined,
    Email: invoiceModel.client?.email || undefined,
    Telefon: invoiceModel.client?.phone || undefined,
    Adresa: invoiceModel.client?.address || undefined,
    Localitate: invoiceModel.client?.city || order.billing.city || undefined,
    Judet: invoiceModel.client?.state || order.billing.state || undefined,
    Tara: invoiceModel.client?.country || "RO",
    TipClient: clientType,
    IdExtern: order.wordPress_Id ? parseInt(order.wordPress_Id) : undefined,
    Strain: invoiceModel.client?.country !== "RO",
    ContBancar: invoiceModel.client?.iban || undefined,
    PlatitorTVA: !!invoiceModel.client?.cif,
  };

  // Map products/content items
  const fgoContent =
    invoiceModel.products?.map((product) => {
      const unitPrice = product.price || 0;
      const quantity = product.quantity || 0;
      const totalPrice = unitPrice * quantity;

      return {
        Denumire: product.name || "",
        CodCentruCost: product.management || undefined,
        Descriere: product.description || undefined,
        PretUnitar: unitPrice,
        ValoareTotala: totalPrice,
        UM: product.measuringUnit || "buc",
        NrProduse: quantity,
        CotaTVA: product.vatPercentage || 19,
      };
    }) || [];

  // Map document type - use FGO nomenclature value if provided
  // The nomenclature data comes as strings (e.g., "Factura", "Aviz") from the API
  const tipFactura: any =
    fgoNomenclature?.tipFactura || documentType?.toUpperCase() || "FACTURA";

  // Map currency - use FGO nomenclature value if provided
  const valuta: any =
    fgoNomenclature?.valuta || order.currency || FgoCurrency.RON;

  // Map client type - use FGO nomenclature value if provided
  const tipClient: any = fgoNomenclature?.tipClient || clientType;

  // Update client with proper type
  const finalClient = {
    ...fgoClient,
    Tip: tipClient,
  };

  // Map VAT - use FGO nomenclature value if provided
  const cotaTVA: any = fgoNomenclature?.tva;

  // Update products with proper VAT from nomenclature if available
  const finalContent = fgoContent.map((product) => {
    return {
      ...product,
      CotaTVA: cotaTVA || product.CotaTVA,
    };
  });

  // Build FGO document (using Romanian field names for FGO API)
  const fgoDocument = {
    Text: invoiceModel.mentions || "",
    Valuta: valuta,
    TipFactura: tipFactura,
    DataEmitere:
      invoiceModel.issueDate || new Date().toISOString().split("T")[0],
    DataScadenta: invoiceModel.dueDate || undefined,
    Numar: invoiceModel.number?.toString() || "",
    Serie: (invoiceModel as any).type === "Factura" ? "1" : "2",
    TvaLaIncasare: false,
    IdExtern: order._id || "",
    Client: finalClient,
    Continut: finalContent,
  };

  return fgoDocument;
};
